import React, { useState, useEffect, useCallback } from 'react';
import { Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Review, Product } from '../../types';
import AddReviewForm from './AddReviewForm';
import ReviewCard from './ReviewCard';
import MediaGallery from './MediaGallery';
import ReviewStats from './ReviewStats';
import { apiRequest, apiUrl } from '../../lib/api';
import toast from 'react-hot-toast';

interface ReviewsSectionProps {
  product: Product;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ product }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [userHasReview, setUserHasReview] = useState(false);
  const [allMedia, setAllMedia] = useState<any[]>([]);

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      const { reviews: reviewsWithMedia } = await apiRequest<{ reviews: Review[] }>(
        `/reviews?productId=${encodeURIComponent(product.id)}`
      );
      const normalizedReviews = reviewsWithMedia.map((review) => ({
        ...review,
        media: review.media?.map((media) => ({
          ...media,
          url: media.url.startsWith('/api/') ? apiUrl(media.url) : media.url,
          thumbnail: media.thumbnail?.startsWith('/api/') ? apiUrl(media.thumbnail) : media.thumbnail,
        })),
      }));

      setReviews(normalizedReviews);

      // Собираем все медиа для галереи
      const allMediaFiles = normalizedReviews.flatMap(review => 
        review.media?.map((media: any) => ({
          ...media,
          reviewId: review.id,
          userName: review.user?.full_name || 'Аноним'
        })) || []
      );
      setAllMedia(allMediaFiles);

    } catch (error: any) {
      console.error('Error loading reviews:', error);
      toast.error(error.message || 'Ошибка загрузки отзывов');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  // Загрузка отзывов
  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // Проверка, есть ли у пользователя отзыв
  useEffect(() => {
    if (user && reviews.length > 0) {
      const hasReview = reviews.some(review => review.user_id === user.id);
      setUserHasReview(hasReview);
    }
  }, [user, reviews]);

  const handleReviewAdded = () => {
    loadReviews();
    setShowAddForm(false);
    toast.success('Отзыв успешно добавлен!');
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    : 0;

  if (loading) {
    return (
      <div className="mt-16">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mx-auto mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-xl">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16">
      <div className="max-w-4xl mx-auto">
        {/* Заголовок и статистика */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Отзывы покупателей
          </h2>
          
          {reviews.length > 0 && (
            <ReviewStats 
              reviews={reviews} 
              averageRating={averageRating}
              totalMedia={allMedia.length}
              onShowMediaGallery={() => setShowMediaGallery(true)}
            />
          )}
        </div>

        {/* Кнопка добавления отзыва */}
        {user && !userHasReview && (
          <div className="mb-8 text-center">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Star className="w-5 h-5" />
              <span>Оставить отзыв</span>
            </button>
          </div>
        )}

        {/* Форма добавления отзыва */}
        {showAddForm && (
          <div className="mb-8">
            <AddReviewForm
              product={product}
              onReviewAdded={handleReviewAdded}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}

        {/* Сообщение для неавторизованных */}
        {!user && (
          <div className="mb-8 text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-blue-800 dark:text-blue-200">
                Войдите в систему, чтобы оставить отзыв о товаре
              </p>
            </div>
          </div>
        )}

        {/* Сообщение если у пользователя уже есть отзыв */}
        {user && userHasReview && (
          <div className="mb-8 text-center">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200">
                Спасибо за ваш отзыв! Один пользователь может оставить только один отзыв на товар.
              </p>
            </div>
          </div>
        )}

        {/* Список отзывов */}
        {reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Star className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Пока нет отзывов
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              Станьте первым, кто оставит отзыв о этом товаре!
            </p>
          </div>
        )}

        {/* Галерея медиа */}
        {showMediaGallery && allMedia.length > 0 && (
          <MediaGallery
            media={allMedia}
            onClose={() => setShowMediaGallery(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ReviewsSection;
